import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, Crown, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("success"); await checkAuth(); return; }
        if (data.status === "expired") { setStatus("failed"); return; }
      } catch (e) {}
      if (attempts.current >= 8) { setStatus("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, checkAuth]);

  return (
    <div className="min-h-screen bg-[#050505] glow-grid flex items-center justify-center px-5">
      <div className="eco-card p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 size={40} className="animate-spin text-[#4d8bff] mx-auto mb-5" />
            <h1 className="font-display text-2xl font-semibold text-white">Confirming your payment…</h1>
            <p className="text-slate-400 mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="h-16 w-16 rounded-2xl bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center mx-auto mb-5"><CheckCircle2 size={32} className="text-emerald-400" /></div>
            <span className="eco-tag inline-flex items-center gap-1.5 text-amber-300 border-amber-400/20 bg-amber-400/10 mb-3"><Crown size={13} /> ECO PRO</span>
            <h1 className="font-display text-2xl font-semibold text-white">Welcome to Pro! 🎉</h1>
            <p className="text-slate-400 mt-2">You've unlocked unlimited mentoring, the Negotiation Simulator, Deal Builder and every advanced lesson.</p>
            <Link to="/dashboard" data-testid="success-dashboard-btn" className="btn-accent mt-7 inline-flex">Go to dashboard <ArrowRight size={16} /></Link>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="h-16 w-16 rounded-2xl bg-red-400/10 border border-red-400/20 flex items-center justify-center mx-auto mb-5"><XCircle size={32} className="text-red-400" /></div>
            <h1 className="font-display text-2xl font-semibold text-white">Payment not completed</h1>
            <p className="text-slate-400 mt-2">We couldn't confirm your payment. If you were charged, it will be reflected shortly.</p>
            <div className="flex gap-3 justify-center mt-7">
              <Link to="/pricing" className="btn-accent">Try again</Link>
              <Link to="/dashboard" className="btn-secondary">Dashboard</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
